/**
 * 学习乐园 · 汉诺塔（game-hanuota.js）
 * ============================================================
 * 「逻辑与推理」组第二作：3 根柱子把 N 个盘从左柱搬到右柱，
 * 大盘不能压小盘，最少步数 = 2ⁿ−1（3 盘 7 步、4 盘 15 步、5 盘 31 步）——
 * 天然教学可解释：结算时把公式和「递归思路」讲给你听。
 *
 * 反馈底线（同本栏目所有游戏）：
 *   合法落盘 → 移动即反馈，不抢话；
 *   非法操作 → 讲清原因：大盘不能压小盘（顶盘直径摆在那）、
 *            空柱没有盘可拿、点了同一根柱等于没动。
 *   每一步为什么不能动，规则本身讲得清，页面只是把它说出来。
 *
 * 口径（页面与 doc 页同步说明）：
 *   - 三档盘数 3/4/5，成绩按档分开存本机（最少步数与完成局数）；
 *   - 不做撤销/自动演示：几步内的事，撤销是负体验；演示会替你思考。
 *
 * 简化红线：不做中断续局，刷新即重开；成绩只存本机 localStorage，不上传。
 *
 * 冒烟钩子：window.STHanoi 暴露纯引擎函数（minMoves / legal），
 * 供确定性用例；交互路径由 scripts/smoke-study.mjs 的对应 section 覆盖。
 */
(function (global) {
  'use strict';

  var PREF_KEY = 'study-hanuota';
  var RECORD_KEY = 'study-hanuota-record';
  var PEG_NAMES = ['左柱', '中柱', '右柱'];

  /* ============ 纯引擎（无 DOM） ============ */

  function minMoves(n) { return Math.pow(2, n) - 1; }

  /**
   * 落盘是否合法（纯判定，供冒烟钩子复用）：
   *   ok:false 时 why 是给用户看的解释——解释规则，不是报错。
   */
  function legal(pegs, from, to) {
    if (from === to) return { ok: false, why: '点的是同一根柱——先点要搬的盘所在的柱，再点要放到哪根柱。' };
    if (!pegs[from].length) return { ok: false, why: PEG_NAMES[from] + '上没有盘。先点有盘的那根柱，把它最上面的盘拿起来。' };
    var d = pegs[from][pegs[from].length - 1];
    var top = pegs[to][pegs[to].length - 1];
    if (top && d > top) {
      return {
        ok: false,
        why: '大盘不能压在小盘上：' + PEG_NAMES[to] + '顶上是 ' + top + ' 号盘，你搬的 ' + d + ' 号盘比它大。'
          + '先把 ' + top + ' 号盘挪走，或者放到别的柱上。',
      };
    }
    return { ok: true, why: '' };
  }

  /* ============ 状态与工具 ============ */

  var root = null;
  var ui = {};
  var S = {
    n: 3, pegs: [], moves: 0, sel: -1, done: false,
    soundOn: true, resetArm: false, resetTimer: null,
  };

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  /* —— 音效（WebAudio 本地合成，与连连看/排排序同一套音色思路）—— */
  /* 手机外放响度补偿（口径统一，由 scripts/boost-sfx-mobile.mjs 批量写入）：
     手机小喇叭对 500Hz 以下钝感、超短音感知响度低——触屏设备把提示音增益翻倍、
     低于 500Hz 的音移高八度、衰减拉长；桌面/耳机维持原响度。 */
  var SFX_MOBILE = false;
  try {
    SFX_MOBILE = (global.matchMedia && global.matchMedia('(pointer: coarse)').matches) ||
      (global.navigator && global.navigator.maxTouchPoints > 0);
  } catch (e) { /* 检测不可用按桌面处理 */ }
  var actx = null;
  function sfx(kind) {
    if (!S.soundOn) return;
    try {
      var AC = global.AudioContext || global.webkitAudioContext;
      if (!AC) return;
      if (!actx) actx = new AC();
      if (actx.state === 'suspended' && actx.resume) actx.resume();
      var t = actx.currentTime;
      var seq;
      if (kind === 'pick') seq = [[440, 0]];
      else if (kind === 'ok') seq = [[620, 0]];
      else if (kind === 'bad') seq = [[200, 0]];
      else seq = [[523, 0], [659, 0.09], [784, 0.18]];   /* win：三连琶音 */
      var type = kind === 'bad' ? 'square' : 'sine';
      for (var i = 0; i < seq.length; i++) {
        var osc = actx.createOscillator();
        var g = actx.createGain();
        osc.type = type;
        osc.frequency.value = SFX_MOBILE && seq[i][0] < 500 ? seq[i][0] * 2 : seq[i][0];
        var t0 = t + seq[i][1];
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(SFX_MOBILE ? 0.27 : 0.09, t0 + 0.012);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + (kind === 'win' && i === seq.length - 1 ? (SFX_MOBILE ? 0.32 : 0.24) : (SFX_MOBILE ? 0.16 : 0.1)));
        osc.connect(g).connect(actx.destination);
        osc.start(t0); osc.stop(t0 + (SFX_MOBILE ? 0.4 : 0.3));
      }
    } catch (e) { /* 音频不可用静默 */ }
  }

  /* —— 偏好与成绩（只存本机）—— */
  function save() {
    try { localStorage.setItem(PREF_KEY, JSON.stringify({ v: 1, n: S.n, soundOn: S.soundOn })); }
    catch (e) { /* 存储不可用静默（无痕模式） */ }
  }
  function restorePrefs() {
    try {
      var d = JSON.parse(localStorage.getItem(PREF_KEY) || '{}');
      if (d && d.v === 1) {
        if (d.n === 3 || d.n === 4 || d.n === 5) S.n = d.n;
        S.soundOn = d.soundOn !== false;
      }
    } catch (e) { /* 忽略脏数据 */ }
  }
  function loadRecord() {
    try { return JSON.parse(localStorage.getItem(RECORD_KEY) || '{}') || {}; }
    catch (e) { return {}; }
  }
  function saveRecord(rec) {
    try { localStorage.setItem(RECORD_KEY, JSON.stringify(rec)); } catch (e) { /* 静默 */ }
  }

  /* ============ 渲染 ============ */

  function renderBoard() {
    ui.board.textContent = '';
    S.pegs.forEach(function (peg, pi) {
      (function (pi) {
        var col = el('div', 'st-ht-peg');
        var stack = el('div', 'st-ht-stack');
        col.setAttribute('role', 'button');
        col.setAttribute('tabindex', '0');
        col.setAttribute('aria-label', PEG_NAMES[pi] + (pi === 2 ? '（把这柱堆满就赢）' : '')
          + '，最上面是 ' + (peg.length ? peg[peg.length - 1] + ' 号盘' : '空的'));
        /* 盘从下往上画：数组末尾是顶盘 */
        peg.slice().reverse().forEach(function (d) {
          var disc = el('div', 'st-ht-disc');
          disc.style.setProperty('--w', String(d / S.n));
          disc.textContent = String(d);
          stack.appendChild(disc);
        });
        col.appendChild(stack);
        col.appendChild(el('div', 'st-ht-base'));
        col.appendChild(el('div', 'st-ht-name', PEG_NAMES[pi]));
        if (S.sel === pi) col.classList.add('is-sel');
        col.addEventListener('click', function () { onPeg(pi); });
        col.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPeg(pi); }
        });
        ui.board.appendChild(col);
      })(pi);
    });
  }

  function renderStatus() {
    if (S.done) return;
    ui.status.textContent = '步数 ' + S.moves + '（最少 ' + minMoves(S.n) + ' 步）'
      + (S.sel >= 0 ? ' · 已拿起' + PEG_NAMES[S.sel] + '顶上的盘，点目标柱放下' : ' · 点一根柱拿起它顶上的盘');
  }

  function recordStat(label, value) {
    var d = el('div', 'st-llk-stat');
    d.appendChild(el('span', 'st-llk-stat-label', label));
    d.appendChild(el('strong', 'st-llk-stat-value', value));
    return d;
  }

  function renderRecord() {
    var box = ui.record;
    box.textContent = '';
    var rec = loadRecord();
    var any = rec[3] && rec[3].best != null || rec[4] && rec[4].best != null || rec[5] && rec[5].best != null;
    if (!any) {
      box.className = 'st-llk-record is-empty';
      box.appendChild(el('p', 'st-llk-record-empty', '还没完成过——搬完一局，这里按盘数记下你的最少步数。'));
      return;
    }
    box.className = 'st-llk-record';
    [3, 4, 5].forEach(function (lv) {
      var r = rec[lv];
      box.appendChild(recordStat(lv + ' 盘最少',
        r && r.best != null ? r.best + ' 步' : '—'));
    });
  }

  function renderButtons() {
    ui.resetBtn.classList.toggle('is-arm', S.resetArm);
    ui.resetBtn.textContent = S.resetArm ? '再点一次确认重置' : '重置成绩';
  }

  function renderLevels() {
    ui.levels.textContent = '';
    [3, 4, 5].forEach(function (lv) {
      var b = el('button', 'st-llk-pack-btn' + (S.n === lv ? ' is-active' : ''));
      b.type = 'button';
      b.setAttribute('data-level', String(lv));
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', S.n === lv ? 'true' : 'false');
      b.textContent = lv + ' 个盘';
      b.addEventListener('click', function () {
        if (S.n === lv) return;
        S.n = lv;
        renderLevels();
        newGame();
        save();
      });
      ui.levels.appendChild(b);
    });
  }

  function renderNote(msg, tone) {
    ui.note.textContent = msg;
    ui.note.className = 'st-llk-note' + (tone ? ' is-' + tone : '');
  }

  /* ============ 交互 ============ */

  function onPeg(pi) {
    if (S.done) return;
    if (S.sel < 0) {
      if (!S.pegs[pi].length) {              /* 空柱：先于「同柱」判定（同柱合法检查无意义） */
        renderNote(PEG_NAMES[pi] + '上没有盘。先点有盘的那根柱，把它最上面的盘拿起来。', 'warn');
        sfx('bad');
        return;
      }
      S.sel = pi;
      renderNote('拿起了' + PEG_NAMES[pi] + '顶上的 ' + S.pegs[pi][S.pegs[pi].length - 1] + ' 号盘，点要放到的柱。', '');
      sfx('pick');
    } else {
      var from = S.sel;
      var chk2 = legal(S.pegs, from, pi);
      if (!chk2.ok) {
        renderNote(chk2.why, 'warn');
        sfx('bad');
        if (from !== pi) S.sel = -1;         /* 非法落盘：盘放回原柱，重新选 */
        renderBoard();
        return;
      }
      var d = S.pegs[from].pop();
      S.pegs[pi].push(d);
      S.moves++;
      S.sel = -1;
      sfx('ok');
      /* 通关也要先渲染终局盘面再弹结算——2026-10-02 修复：
         此前 finish() 里不渲染、这里又 return，胜利提示出来了盘却停在上一帧 */
      if (pi === 2 && S.pegs[2].length === S.n) { renderBoard(); finish(); return; }
      renderNote(d + ' 号盘从' + PEG_NAMES[from] + '移到' + PEG_NAMES[pi] + '。接着搬。', '');
    }
    renderBoard();
    renderStatus();
  }

  function finish() {
    S.done = true;
    var least = minMoves(S.n);
    var rec = loadRecord();
    var r = rec[S.n] || { best: null, plays: 0 };
    r.plays++;
    var isBest = r.best == null || S.moves < r.best;
    if (isBest) r.best = S.moves;
    rec[S.n] = r;
    saveRecord(rec);
    ui.status.textContent = '完成！' + S.moves + ' 步（最少 ' + least + ' 步）';
    ui.status.className = 'st-status is-over';
    var teach;
    if (S.moves === least) {
      teach = S.moves + ' 步，正好是最少步数——' + S.n + ' 个盘最少就是 2^' + S.n + '−1 = ' + least
        + ' 步。你把「先把上面 ' + (S.n - 1) + ' 层搬去中转柱」的递归思路用对了。';
    } else {
      teach = '用了 ' + S.moves + ' 步，最少只要 ' + least + ' 步（2^' + S.n + '−1）。省步的关键不是试，而是先想：'
        + '要把 ' + S.n + ' 个盘搬到右柱，得先把上面 ' + (S.n - 1) + ' 个盘整个搬到中柱——这件事本身又是一个小一号的汉诺塔。';
    }
    renderNote(teach + (isBest ? '这是 ' + S.n + ' 个盘档的最好成绩！' : ''), 'ok');
    sfx('win');
    renderRecord();
  }

  function newGame() {
    S.pegs = [[], [], []];
    for (var d = S.n; d >= 1; d--) S.pegs[0].push(d);
    S.moves = 0;
    S.sel = -1;
    S.done = false;
    ui.status.className = 'st-status';
    ui.status.textContent = '步数 0（最少 ' + minMoves(S.n) + ' 步） · 点一根柱拿起它顶上的盘';
    renderNote('把 ' + S.n + ' 个盘从左柱搬到右柱：大盘不能压小盘。先想再搬——省步靠思路，不靠手快。', '');
    renderBoard();
    renderRecord();
    renderButtons();
  }

  /* ============ boot ============ */

  function boot() {
    root = document.querySelector('[data-st-game]');
    if (!root) return;
    ui.board = root.querySelector('[data-st-ht-board]');
    ui.status = root.querySelector('[data-st-status]');
    ui.note = root.querySelector('[data-st-note]');
    ui.levels = root.querySelector('[data-st-ht-levels]');
    ui.record = root.querySelector('[data-st-record]');
    ui.newBtn = root.querySelector('[data-st-new]');
    ui.resetBtn = root.querySelector('[data-st-reset]');
    if (!ui.board) return;

    restorePrefs();
    renderLevels();
    bindSoundPicker();

    ui.newBtn.addEventListener('click', function () { newGame(); });
    ui.resetBtn.addEventListener('click', resetRecord);

    newGame();
  }

  function resetRecord() {
    if (!S.resetArm) {
      S.resetArm = true;
      renderButtons();
      if (S.resetTimer) clearTimeout(S.resetTimer);
      S.resetTimer = setTimeout(function () { S.resetArm = false; S.resetTimer = null; renderButtons(); }, 3000);
      return;
    }
    S.resetArm = false;
    if (S.resetTimer) { clearTimeout(S.resetTimer); S.resetTimer = null; }
    saveRecord({});
    renderRecord();
    renderButtons();
    renderNote('已重置汉诺塔的本机成绩。', '');
  }

  /* 音效选择器（口径同排排序：tool-segmented 双按钮） */
  function bindSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      (function (b) {
        b.addEventListener('click', function () {
          S.soundOn = b.getAttribute('data-st-sound') === 'on';
          syncSoundPicker();
          save();
          if (S.soundOn) sfx('ok');
        });
      })(btns[i]);
    }
    syncSoundPicker();
  }
  function syncSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      var on = (btns[i].getAttribute('data-st-sound') === 'on') === S.soundOn;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }

  global.STHanoi = {
    minMoves: minMoves,
    legal: legal,
  };
})(typeof window !== 'undefined' ? window : this);
